<template>
  <section class="dashboard-panel report-export" aria-labelledby="report-export-title">
    <header class="report-export__header">
      <span class="report-export__icon"><i class="ph ph-file-xls" aria-hidden="true"></i></span>
      <div>
        <span class="report-export__eyebrow">Seus dados com você</span>
        <h2 id="report-export-title">Relatório em Excel</h2>
      </div>
    </header>
    <p class="report-export__description">Escolha o mês para baixar suas entradas, gastos e recorrentes previstos, com totais e saldo calculados.</p>

    <form class="report-export__form" @submit.prevent="exportReport">
      <label for="report-month">
        <span>Mês</span>
        <select id="report-month" v-model.number="month" :disabled="exporting" required>
          <option v-for="item in months" :key="item.value" :value="item.value">{{ item.label }}</option>
        </select>
      </label>
      <label for="report-year">
        <span>Ano</span>
        <input id="report-year" v-model.number="year" type="number" min="2000" max="2100" step="1" inputmode="numeric" :disabled="exporting" required />
      </label>
      <button type="submit" :disabled="exporting" :aria-busy="exporting">
        <i :class="exporting ? 'ph ph-spinner report-export__spinner' : 'ph ph-download-simple'" aria-hidden="true"></i>
        {{ exporting ? 'Gerando planilha…' : 'Baixar Excel' }}
      </button>
    </form>
    <p class="report-export__hint">Arquivo .xlsx com resumo e abas separadas. Recorrentes são previsões, conforme os cadastros atuais.</p>
    <p v-if="error" class="report-export__feedback report-export__feedback--error" role="alert">{{ error }}</p>
    <p v-else-if="success" class="report-export__feedback" role="status">{{ success }}</p>
    <span v-else-if="exporting" class="report-export__feedback" role="status">Preparando todos os lançamentos do mês selecionado…</span>
  </section>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import { useStore } from "vuex";
import { financeContext } from "../../services/financeScope";
import { downloadMonthlyReport, loadMonthlyReport } from "../../utils/financeMonthlyReport";

export default defineComponent({
  name: "MonthlyReportExportCard",
  setup() {
    const store = useStore();
    const now = new Date();
    const month = ref(now.getMonth() + 1);
    const year = ref(now.getFullYear());
    const exporting = ref(false);
    const error = ref("");
    const success = ref("");
    const months = Array.from({ length: 12 }, (_, index) => ({
      value: index + 1,
      label: new Intl.DateTimeFormat("pt-BR", { month: "long" }).format(new Date(2026, index, 1)).replace(/^./, char => char.toUpperCase()),
    }));

    async function exportReport() {
      if (exporting.value) return;
      exporting.value = true;
      error.value = "";
      success.value = "";
      const revision = financeContext.revision;
      const validView = () => revision === financeContext.revision;
      try {
        const data = await loadMonthlyReport(
          month.value, year.value,
          params => { if (!validView()) throw new Error("Visão alterada."); return store.dispatch("fetchFinanceCategoryTransactions", params); },
          () => store.dispatch("fetchFinanceRecurringExpensesList", { status: "all" })
        );
        data.viewLabel = ({ self: "Meus gastos", partner: `Gastos de ${store.getters.getCouplePartner?.name || "parceiro"}`, couple: "Nós dois" } as Record<string, string>)[store.getters.getFinanceScope];
        await downloadMonthlyReport(data, validView);
        success.value = `Planilha de ${months[data.month - 1].label.toLowerCase()} de ${data.year} gerada com sucesso.`;
      } catch {
        error.value = "Não foi possível gerar a planilha completa. Confira o mês e o ano e tente novamente.";
      } finally {
        exporting.value = false;
      }
    }

    return { month, year, months, exporting, error, success, exportReport };
  },
});
</script>

<style scoped>
.report-export { grid-column: 1 / -1; }
.report-export__header { align-items: center; display: flex; gap: 12px; }
.report-export__icon { align-items: center; background: #e7f8f2; border-radius: 14px; color: var(--dashboard-accent-dark); display: inline-flex; flex: 0 0 46px; font-size: 26px; height: 46px; justify-content: center; }
.report-export__eyebrow { color: var(--dashboard-accent-dark); display: block; font-size: 10px; font-weight: 800; letter-spacing: .08em; margin-bottom: 4px; text-transform: uppercase; }
.report-export h2 { color: var(--dashboard-ink); font-size: 19px; font-weight: 800; letter-spacing: -.03em; margin: 0; }
.report-export__description { color: var(--dashboard-muted); font-size: 13px; line-height: 1.6; margin: 16px 0 20px; max-width: 680px; }
.report-export__form { align-items: end; display: grid; gap: 12px; grid-template-columns: minmax(0, 1fr) minmax(100px, .6fr) minmax(160px, .8fr); }
.report-export__form label { color: var(--dashboard-ink); display: grid; font-size: 12px; font-weight: 700; gap: 7px; min-width: 0; }
.report-export__form select, .report-export__form input { background: var(--dashboard-surface); border: 1px solid var(--dashboard-border); border-radius: 12px; color: var(--dashboard-ink); font: inherit; height: 46px; min-width: 0; padding: 0 12px; width: 100%; }
.report-export__form button { align-items: center; background: var(--dashboard-accent-dark); border: 0; border-radius: 12px; color: #fff; display: flex; font-size: 13px; font-weight: 800; gap: 8px; justify-content: center; min-height: 46px; padding: 10px 16px; }
.report-export__form button i { font-size: 20px; }
.report-export__form button:hover:not(:disabled) { background: #045f4a; }
.report-export__form button:disabled { cursor: wait; opacity: .7; }
.report-export__form :focus-visible { outline: 2px solid var(--dashboard-accent); outline-offset: 3px; }
.report-export__hint { color: var(--dashboard-muted); font-size: 11px; line-height: 1.6; margin: 14px 0 0; }
.report-export__feedback { color: var(--dashboard-accent-dark); display: block; font-size: 12px; line-height: 1.6; margin: 12px 0 0; }
.report-export__feedback--error { color: #bd3d3d; }
.report-export__spinner { animation: report-export-spin 1s linear infinite; }
@keyframes report-export-spin { to { transform: rotate(360deg); } }
@media (max-width: 575px) { .report-export__form { grid-template-columns: minmax(0, 1fr) minmax(100px, .65fr); }.report-export__form button { grid-column: 1 / -1; } }
@media (prefers-reduced-motion: reduce) { .report-export__spinner { animation: none; } }
</style>
